import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { findCategoryArticle } from "@/data/categoryArticles";

// Turn plain URLs into <a> tags. Keeps existing text intact.
const URL_RE = /(https?:\/\/[^\s)]+?|www\.[^\s)]+?)([.,;:!?)]*)(?=\s|$)/g;
const linkify = (text) => {
  if (!text) return text;
  const nodes = [];
  let lastIdx = 0;
  let key = 0;
  let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > lastIdx) nodes.push(text.slice(lastIdx, m.index));
    const url = m[1];
    const trailing = m[2] || "";
    const href = url.startsWith("http") ? url : `https://${url}`;
    nodes.push(
      <a
        key={key++}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2"
        style={{ color: "var(--wofy-primary)" }}
      >
        {url}
      </a>,
    );
    if (trailing) nodes.push(trailing);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) nodes.push(text.slice(lastIdx));
  return nodes;
};

export default function CategoryArticle() {
  const { slug } = useParams();
  const a = findCategoryArticle(slug);
  if (!a) return <Navigate to="/" replace />;

  const cta = a.cta || {};

  return (
    <article data-testid="category-article" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-16">
      <Link
        to="/"
        data-testid="back-to-home"
        className="inline-flex items-center gap-2 text-sm mb-8"
        style={{ color: "var(--wofy-text-soft)" }}
      >
        <ArrowLeft className="w-4 h-4" /> Back home
      </Link>

      {/* HERO */}
      <header className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
        <div className={a.hero_image ? "lg:col-span-7" : "lg:col-span-12"}>
          <div className="overline">{a.overline}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-4">
            {a.label}{" "}
            <span className="italic" style={{ color: "var(--wofy-primary)" }}>&mdash;</span>{" "}
            <span style={{ color: "var(--wofy-text-soft)" }}>{a.title}</span>
          </h1>
          {a.location && <div className="overline mt-6">{a.location}</div>}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {cta.href && (
              <Link to={cta.href} data-testid="cta-primary" className="wofy-btn-primary">
                {cta.label || "Get involved"} <ArrowUpRight className="w-4 h-4" />
              </Link>
            )}
            {cta.secondary_href && (
              <Link to={cta.secondary_href} data-testid="cta-secondary" className="wofy-btn-ghost">
                {cta.secondary_label || "Volunteer"}
              </Link>
            )}
          </div>
        </div>

        {a.hero_image && (
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
              <img src={a.hero_image} alt={a.label} className="w-full h-[380px] lg:h-[440px] object-cover" />
            </div>
          </div>
        )}
      </header>

      {/* INTRO */}
      {(a.intro || a.lead) && (
        <div className="mt-16 md:mt-20 grid lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-3">
            <div className="overline">Introduction</div>
          </div>
          <div className="lg:col-span-9 space-y-5">
            {a.intro && (
              <p className="text-lg leading-relaxed font-display" style={{ color: "var(--wofy-text)" }}>
                {a.intro}
              </p>
            )}
            {a.lead && (
              <p className="text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
                {a.lead}
              </p>
            )}
          </div>
        </div>
      )}

      {/* SECTIONS */}
      {a.sections?.length > 0 && (
        <div className="mt-16 md:mt-20 space-y-14 md:space-y-16">
          {a.sections.map((sec, si) => (
            <section key={`${sec.heading}-${si}`} className="grid lg:grid-cols-12 gap-8 md:gap-12">
              <div className="lg:col-span-3">
                <h2 className="font-display text-2xl leading-tight" style={{ color: "var(--wofy-text)" }}>
                  {sec.heading}
                </h2>
              </div>
              <div className="lg:col-span-9 space-y-5">
                {sec.body?.map((p, i) => (
                  <p key={`${si}-p-${i}`} className="text-base leading-relaxed" style={{ color: "var(--wofy-text)" }}>
                    {linkify(p)}
                  </p>
                ))}
                {sec.list?.length > 0 && (
                  <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    {sec.list.map((li, li_i) => (
                      <li key={`${si}-l-${li_i}`} className="flex items-start gap-3 text-base leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--wofy-primary)" }} />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {sec.outro && (
                  <p className="text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
                    {linkify(sec.outro)}
                  </p>
                )}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* CLOSING CTA */}
      {(cta.href || cta.secondary_href) && (
        <aside
          className="mt-20 rounded-3xl p-8 md:p-12 relative overflow-hidden"
          style={{ background: "var(--wofy-primary)", color: "#FDFBF7" }}
        >
          <div className="overline text-white/70">JOIN THE MOVEMENT</div>
          <h3 className="font-display text-3xl md:text-4xl mt-3 max-w-2xl leading-tight">
            {a.closing_heading || `Help us make ${a.label.toLowerCase()} accessible for every family.`}
          </h3>
          <p className="mt-4 max-w-2xl text-white/85">
            {a.closing_body ||
              "Give time, funds, or skills — every contribution keeps a family safer, healthier, and closer to the life they deserve."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {cta.href && (
              <Link to={cta.href} className="rounded-full px-6 py-3 text-sm font-medium" style={{ background: "#FDFBF7", color: "var(--wofy-text)" }}>
                {cta.label || "Get involved"}
              </Link>
            )}
            {cta.secondary_href && (
              <Link to={cta.secondary_href} className="rounded-full px-6 py-3 text-sm font-medium border border-white/40 text-white hover:bg-white/10 transition-colors">
                {cta.secondary_label || "Volunteer"}
              </Link>
            )}
          </div>
        </aside>
      )}
    </article>
  );
}
